// client/src/components/AuctionForm.js
// Always lists an auction from an existing inventory product — `products` is
// required, so a listing can never be created for something that isn't real
// stock. Picking a product pre-fills name/description/category/photo; only
// the auction-specific fields (price, dates) need to be entered fresh.
import React, { useState } from 'react';
import { TextField, Button, Stack, Alert, Box, Avatar, MenuItem, Typography } from '@mui/material';
import { getImageUrl } from '../utils/media';
import { useCategories } from '../context/CategoryContext';

const toLocalDatetimeInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const defaultEndTime = () => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return toLocalDatetimeInputValue(d);
};

const AuctionForm = ({ products, onSave, onCancel }) => {
  const { categoryNames, subcategoriesFor } = useCategories();
  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    description: '',
    category: '',
    subcategory: '',
    imageUrl: '',
    startingPrice: '',
    buyNowPrice: '',
    reservePrice: '',
    endTime: defaultEndTime(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category') {
      setFormData((prev) => ({ ...prev, category: value, subcategory: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    const product = products.find((p) => String(p.id) === String(productId));
    setFormData((prev) => ({
      ...prev,
      productId,
      productName: product?.name || '',
      description: product?.description || '',
      category: product?.category || '',
      subcategory: product?.subcategory || '',
      imageUrl: product?.imageUrl || '',
    }));
  };

  const subcategoryOptions = subcategoriesFor(formData.category);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('productId', formData.productId);
      data.append('productName', formData.productName);
      data.append('description', formData.description);
      if (formData.category) data.append('category', formData.category);
      if (formData.subcategory) data.append('subcategory', formData.subcategory);
      if (formData.imageUrl) data.append('imageUrl', formData.imageUrl);
      data.append('startingPrice', formData.startingPrice);
      data.append('endTime', new Date(formData.endTime).toISOString());
      if (formData.buyNowPrice) data.append('buyNowPrice', formData.buyNowPrice);
      if (formData.reservePrice) data.append('reservePrice', formData.reservePrice);
      await onSave(data);
    } catch (err) {
      setError(err.message || 'Failed to create auction.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <TextField select label="Product to Auction" name="productId" value={formData.productId} onChange={handleProductChange} required fullWidth>
        {products.length === 0 && (
          <MenuItem value="" disabled>You have no products in your inventory yet</MenuItem>
        )}
        {products.map((p) => (
          <MenuItem key={p.id} value={p.id} disabled={Boolean(p.active_auction_id)}>
            {p.name}{p.active_auction_id ? ' (already up for auction)' : ''}
          </MenuItem>
        ))}
      </TextField>

      {formData.productId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={getImageUrl(formData.imageUrl)} variant="rounded" sx={{ width: 56, height: 56 }} />
          <Typography variant="body2" color="text.secondary">
            Using this product's photo from your inventory.
          </Typography>
        </Box>
      )}

      <TextField label="Auction Title" name="productName" value={formData.productName} onChange={handleChange} required fullWidth helperText="Defaults to the product name — feel free to customize it for the auction" />
      <TextField label="Description" name="description" value={formData.description} onChange={handleChange} placeholder="Condition, history, etc." required fullWidth multiline minRows={3} />
      <Stack direction="row" spacing={2}>
        <TextField select label="Category" name="category" value={formData.category || ''} onChange={handleChange} fullWidth>
          {categoryNames.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField
          select label="Subcategory" name="subcategory" value={formData.subcategory || ''} onChange={handleChange}
          fullWidth disabled={subcategoryOptions.length === 0}
        >
          <MenuItem value="">(None)</MenuItem>
          {subcategoryOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Stack>
      <TextField label="Starting Price (R)" name="startingPrice" type="number" inputProps={{ min: 0.01, step: 0.01 }} value={formData.startingPrice} onChange={handleChange} required fullWidth />
      <Stack direction="row" spacing={2}>
        <TextField
          label="Buy It Now Price (R, optional)" name="buyNowPrice" type="number" inputProps={{ step: 0.01 }}
          value={formData.buyNowPrice} onChange={handleChange} fullWidth
          helperText="Instantly ends the auction if met"
        />
        <TextField
          label="Reserve Price (R, optional)" name="reservePrice" type="number" inputProps={{ step: 0.01 }}
          value={formData.reservePrice} onChange={handleChange} fullWidth
          helperText="Minimum price to sell"
        />
      </Stack>
      <TextField label="Auction Ends At" name="endTime" type="datetime-local" value={formData.endTime} onChange={handleChange} required fullWidth InputLabelProps={{ shrink: true }} />

      <Stack direction="row" spacing={2}>
        <Button type="submit" variant="contained" disabled={submitting || !formData.productId}>
          {submitting ? 'Creating...' : 'Create Auction'}
        </Button>
        <Button type="button" variant="text" onClick={onCancel}>Cancel</Button>
      </Stack>
    </Stack>
  );
};

export default AuctionForm;
